import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthLogin, GuestLogin } from './state/lobby.actions';
import { AuthService } from './services/auth.service';
import { environment } from '../environments/environment';
import { WebSocketState } from './state/websocket.state';
import { distinctUntilChanged, filter } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  template: '<router-outlet />',
})
export class App implements OnInit {
  private store = inject(Store);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  async ngOnInit() {
    console.log('[App] WebSocket URL:', environment.wsUrl);

    // The visual-regression screenshot route provides its own mock Store.
    // Do not start auth or the real websocket when Playwright visits it.
    if (location.pathname.endsWith('/screenshot')) {
      document.getElementById('boot-fallback')?.remove();
      return;
    }

    if (!environment.wsUrl) {
      const el = document.getElementById('boot-fallback-log');
      if (el) el.textContent += '[error] environment.wsUrl is not set\n';
    }

    this.store.select(WebSocketState.lastError).pipe(
      distinctUntilChanged(),
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((err) => {
      this.snackBar.open(err, 'Dismiss', { duration: 10_000 });
    });

    try {
      await this.authService.init();
      const token = this.authService.getToken();
      if (token) {
        this.store.dispatch(new AuthLogin(token));
        document.getElementById('boot-fallback')?.remove();
        return;
      }
    } catch (err) {
      console.warn('[App] auth init failed, falling back to guest login', err);
      this.snackBar.open(
        `Auth unavailable, continuing as guest: ${(err as Error).message ?? err}`,
        'Dismiss',
        { duration: 8000 },
      );
    }
    this.store.dispatch(new GuestLogin());

    const bootFallback = document.getElementById('boot-fallback');
    if (bootFallback) bootFallback.remove();
  }
}
